import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatChipsModule } from "@angular/material/chips";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Subscription } from "rxjs";
import { ProductType } from "../../models/product-type.model";
import { ProductTypeService } from "../../services/product-type.service";

@Component({
  selector: "app-product-type-selector",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./product-type-selector.component.html",
  styleUrl: "./product-type-selector.component.css",
})
export class ProductTypeSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label = "Product Types";
  @Input() placeholder = "Search product types...";
  @Input() loadingMessage = "Loading product types…";
  @Input() preselectedIds: number[] = [];
  /** When true, options render in a fixed scrollable panel (better inside dialogs). */
  @Input() panelMode = false;
  /** When false, only the field suffix spinner indicates loading (less vertical clutter). */
  @Input() showInlineLoadingBar = true;
  /** When true, closes the options list after each selection. */
  @Input() collapseOnSelect = true;
  /** When true, hides the search field while at least one product type is selected. */
  @Input() hideInputWhenSelected = false;

  @Output() selectionChange = new EventEmitter<number[]>();

  @ViewChild("searchInput") searchInputRef?: ElementRef<HTMLInputElement>;

  allProductTypes: ProductType[] = [];
  selectedProductTypes: ProductType[] = [];
  filteredProductTypes: ProductType[] = [];
  searchQuery = "";
  dropdownOpen = false;
  highlightedIndex = -1;
  loading = false;
  loadError = "";

  private subs = new Subscription();
  private dropdownHover = false;

  constructor(
    private productTypeService: ProductTypeService,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.subs.add(
      this.productTypeService.getProductTypes().subscribe({
        next: (types) => {
          this.allProductTypes = types;
          this.applyPreselection();
          this.filterProductTypes();
          this.loading = false;
        },
        error: () => {
          this.loadError = "Failed to load product types.";
          this.loading = false;
        },
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["preselectedIds"] && this.allProductTypes.length) {
      this.applyPreselection();
      this.filterProductTypes();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.hostRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.filterProductTypes();
    this.openDropdown();
  }

  onInputFocus(): void {
    if (this.loading) {
      return;
    }
    this.openDropdown();
  }

  onInputBlur(): void {
    setTimeout(() => {
      if (!this.dropdownHover) {
        this.closeDropdown();
      }
    }, 150);
  }

  onDropdownMouseEnter(): void {
    this.dropdownHover = true;
  }

  onDropdownMouseLeave(): void {
    this.dropdownHover = false;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.loading) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeDropdown();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!this.dropdownOpen) {
        this.openDropdown();
      }
      if (this.filteredProductTypes.length) {
        this.highlightedIndex = Math.min(
          this.highlightedIndex + 1,
          this.filteredProductTypes.length - 1,
        );
      }
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (this.filteredProductTypes.length) {
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
      }
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (this.highlightedIndex >= 0 && this.filteredProductTypes[this.highlightedIndex]) {
        this.toggleProductType(this.filteredProductTypes[this.highlightedIndex]);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.closeDropdown();
      return;
    }

    if (event.key === "Backspace" && !this.searchQuery.trim() && this.selectedProductTypes.length) {
      this.removeProductType(this.selectedProductTypes[this.selectedProductTypes.length - 1]);
    }
  }

  toggleProductType(productType: ProductType): void {
    if (this.loading) {
      return;
    }
    if (this.isSelected(productType)) {
      this.removeProductType(productType);
      return;
    }

    this.selectedProductTypes = [...this.selectedProductTypes, productType];
    this.emitSelection();
    this.filterProductTypes();
    this.searchQuery = "";

    if (this.collapseOnSelect) {
      this.closeDropdown();
      this.searchInputRef?.nativeElement.blur();
      return;
    }

    this.openDropdown();
    this.searchInputRef?.nativeElement.focus();
  }

  removeProductType(productType: ProductType): void {
    this.selectedProductTypes = this.selectedProductTypes.filter((pt) => pt.id !== productType.id);
    this.emitSelection();
    this.filterProductTypes();
    this.closeDropdown();

    if (this.hideInputWhenSelected && this.selectedProductTypes.length === 0) {
      queueMicrotask(() => this.searchInputRef?.nativeElement.focus());
    }
  }

  isSelected(productType: ProductType): boolean {
    return this.selectedProductTypes.some((pt) => pt.id === productType.id);
  }

  rowLabel(productType: ProductType): string {
    return `[${productType.name}] ${productType.description}`;
  }

  private applyPreselection(): void {
    if (!this.preselectedIds?.length) return;
    const idSet = new Set(this.preselectedIds);
    this.selectedProductTypes = this.allProductTypes.filter((pt) => idSet.has(pt.id));
    this.emitSelection();
  }

  private filterProductTypes(): void {
    const q = this.searchQuery.trim().toLowerCase();
    const filtered = this.allProductTypes.filter((pt) => {
      if (!q) return true;
      return (
        pt.name.toLowerCase().includes(q) ||
        pt.description.toLowerCase().includes(q)
      );
    });
    this.filteredProductTypes = filtered;
    if (this.highlightedIndex >= filtered.length) {
      this.highlightedIndex = filtered.length ? 0 : -1;
    }
  }

  private openDropdown(): void {
    this.dropdownOpen = true;
    this.filterProductTypes();
    if (this.filteredProductTypes.length && this.highlightedIndex < 0) {
      this.highlightedIndex = 0;
    }
  }

  private closeDropdown(): void {
    this.dropdownOpen = false;
    this.highlightedIndex = -1;
  }

  private emitSelection(): void {
    this.selectionChange.emit(this.selectedProductTypes.map((pt) => pt.id));
  }
}
